import {
    Accordion,
    AccordionButton,
    AccordionIcon,
    AccordionItem,
    AccordionPanel,
    Badge,
    Box,
    Button,
    CloseButton,
    Flex,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Text,
    Tooltip,
} from "@chakra-ui/react";
import { FunctionComponent, useRef, useState, useEffect } from "react";
import { TransactionForm } from "./TransactionForm";
import { Transaction } from "../../../api/transactions/transactionApi";
import { Entity } from "../../../api/entities/entityApi";
import { useDeleteTransaction, useUpdateTransaction } from "../../../api/transactions/transactionQueries";
import { pluralize } from "../../../utils";
import { MIN_TRANSACTIONS, MAX_TRANSACTIONS, MODAL_CONTENT_SIZING, MODAL_SIZING } from "../../../utils/constants";

interface TransactionModalProps {
    transactionNumber?: number;
    entities?: Entity[];
    show: boolean;
    onClose: () => void;
    rowData?: Transaction;
    clientId?: string;
    onSave?: (data: {
        name: string;
        category: string;
        transactionType: string;
        serviceProvider: string;
        serviceRecipient: string;
        method: string;
        testedParty: string;
        benchmarkAnalysis: string;
    }) => void;
}

export const TransactionModal: FunctionComponent<TransactionModalProps> = ({ show, onClose, rowData, transactionNumber, entities, clientId }) => {
    const formRefs = useRef<(HTMLFormElement | null)[]>([]);
    const [transactionCount, setTransactionCount] = useState(transactionNumber ?? 1);
    const [transactionIds, setTransactionIds] = useState<string[]>([]);
    const [formErrors, setFormErrors] = useState<Record<string, boolean>>({});
    const [accordionIndex, setAccordionIndex] = useState<number[]>([0]);
    const [submittedTransactions, setSubmittedTransactions] = useState<Set<string>>(new Set());
    const { mutateAsync: deleteTransactionMutation, isPending: isDeletingTransaction } = useDeleteTransaction();
    const { isPending: isUpdatingTransaction } = useUpdateTransaction();

    // Determine if any transaction operation is in progress
    const isAnyTransactionPending = isUpdatingTransaction;

    // Reset transaction count and form refs when transactionNumber prop changes
    useEffect(() => {
        if (transactionNumber !== undefined) {
            setTransactionCount(transactionNumber);
            formRefs.current = []; // Reset form refs to force component remount
            // Generate stable IDs for transactions
            const newIds = Array.from({ length: transactionNumber }, (_, i) => `transaction-${crypto.randomUUID()}-${i}`);
            setTransactionIds(newIds);
            // Clear form errors for new transaction set
            setFormErrors({});
            // Clear submitted transactions for new transaction set
            setSubmittedTransactions(new Set());
            // Set only first accordion item to be open by default
            setAccordionIndex([0]);
        }
    }, [transactionNumber]);

    // Initialize transaction IDs when component mounts
    useEffect(() => {
        if (transactionIds.length === 0) {
            setTransactionIds(Array.from({ length: transactionCount }, (_, i) => `transaction-${Date.now()}-${i}`));
            // Set only first accordion item to be open by default
            setAccordionIndex([0]);
            // Clear submitted transactions for new transaction set
            setSubmittedTransactions(new Set());
        }
    }, [transactionCount, transactionIds.length]);

    // Auto-close modal when all transactions have been submitted successfully
    useEffect(() => {
        // Only auto-close for multi-transaction scenarios (not editing existing transactions)
        if (!rowData && transactionIds.length > 0 && submittedTransactions.size === transactionIds.length) {
            // Add a small delay to allow users to see the success state before closing
            const timer = setTimeout(() => {
                onClose();
            }, 1000);

            return () => clearTimeout(timer);
        }
    }, [submittedTransactions.size, transactionIds.length, rowData, onClose]);

    const handleSubmitAllForms = () => {
        formRefs.current.forEach(form => {
            if (form) {
                form.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
            }
        });
    };

    const handleDelete = async () => {
        if (rowData) {
            await deleteTransactionMutation(rowData.id);
            onClose();
        }
    };

    const handleRemoveTransaction = (indexToRemove: number) => {
        if (transactionCount <= MIN_TRANSACTIONS) return; // Prevent removing the last transaction

        const transactionIdToRemove = transactionIds[indexToRemove];

        setTransactionCount(prev => prev - 1);

        // Remove the specific transaction ID and form ref
        setTransactionIds(prev => prev.filter((_, index) => index !== indexToRemove));
        formRefs.current = formRefs.current.filter((_, index) => index !== indexToRemove);

        // Remove form errors for the removed transaction
        setFormErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[transactionIdToRemove];
            return newErrors;
        });

        // Remove from submitted transactions
        setSubmittedTransactions(prev => {
            const newSubmitted = new Set(prev);
            newSubmitted.delete(transactionIdToRemove);
            return newSubmitted;
        });

        // Update accordion index to account for removed item
        setAccordionIndex(
            prev =>
                prev
                    .filter(idx => idx !== indexToRemove) // Remove the removed index
                    .map(idx => (idx > indexToRemove ? idx - 1 : idx)), // Shift indices down
        );
    };

    const handleAddTransaction = () => {
        if (transactionCount >= MAX_TRANSACTIONS) return; // Prevent adding too many transactions

        setTransactionCount(prev => prev + 1);
        setTransactionIds(prev => [...prev, `transaction-${Date.now()}-${prev.length}`]);
        // Keep existing accordion state when adding new transaction
    };

    const handleFormValidation = (transactionId: string, hasErrors: boolean) => {
        setFormErrors(prev => ({
            ...prev,
            [transactionId]: hasErrors,
        }));
    };

    const handleFormSubmitSuccess = (transactionId: string) => {
        setFormErrors(prev => ({
            ...prev,
            [transactionId]: false,
        }));

        // Mark this transaction as submitted
        setSubmittedTransactions(prev => new Set(prev).add(transactionId));
    };

    return (
        <Modal isOpen={show} onClose={onClose} size={MODAL_SIZING} isCentered motionPreset="slideInBottom">
            <ModalOverlay />
            <ModalContent style={{ ...MODAL_CONTENT_SIZING }} overflowY="auto">
                <ModalHeader>
                    <Flex alignItems="center">
                        <Text>{rowData ? "Edit Transaction" : `Add ${transactionCount} Transaction${transactionCount > MIN_TRANSACTIONS ? "s" : ""}`}</Text>
                        {!rowData && transactionCount > MIN_TRANSACTIONS && (
                            <Text fontSize="sm" pl={3}>
                                Use the accordion below to expand/collapse transaction forms
                            </Text>
                        )}
                    </Flex>
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    {!rowData ? (
                        <>
                            <Accordion allowMultiple index={accordionIndex} onChange={expandedIndex => setAccordionIndex(expandedIndex as number[])}>
                                {transactionIds.map((transactionId, i) => (
                                    <AccordionItem key={transactionId} border="1px solid" borderColor="gray.200" borderRadius="md" mb={4}>
                                        <AccordionButton
                                            bg="chakra-body-bg"
                                            _hover={{
                                                bg: "gray.300",
                                            }}
                                            _expanded={{
                                                bg: "gray.300",
                                            }}
                                            borderTopRadius="md"
                                        >
                                            <Box flex="1" textAlign="left">
                                                <Flex justifyContent="space-between" alignItems="center" width="100%">
                                                    <Flex alignItems="center" gap={2}>
                                                        <Text fontWeight="medium">Transaction {i + 1}</Text>
                                                        {formErrors[transactionId] && (
                                                            <Badge colorScheme="red" size="sm">
                                                                Error
                                                            </Badge>
                                                        )}
                                                    </Flex>
                                                    {transactionCount > MIN_TRANSACTIONS && (
                                                        <Tooltip label="Remove this transaction">
                                                            <CloseButton
                                                                size="sm"
                                                                onClick={e => {
                                                                    e.stopPropagation();
                                                                    handleRemoveTransaction(i);
                                                                }}
                                                                ml={2}
                                                                color="red.500"
                                                                _hover={{
                                                                    color: "red.600",
                                                                    bg: "red.50",
                                                                }}
                                                                aria-label="Remove transaction"
                                                            />
                                                        </Tooltip>
                                                    )}
                                                </Flex>
                                            </Box>
                                            <AccordionIcon />
                                        </AccordionButton>
                                        <AccordionPanel pb={4}>
                                            <TransactionForm
                                                key={`form-${transactionId}-${transactionNumber}`}
                                                clientId={clientId}
                                                entities={entities}
                                                rowData={rowData}
                                                transactionNumber={i + 1}
                                                ref={el => (formRefs.current[i] = el)}
                                                onClose={onClose}
                                                onValidationChange={(hasErrors: boolean) => handleFormValidation(transactionId, hasErrors)}
                                                onSubmitSuccess={() => handleFormSubmitSuccess(transactionId)}
                                            />
                                        </AccordionPanel>
                                    </AccordionItem>
                                ))}
                            </Accordion>
                            <Box my="4rem" textAlign="center">
                                <Button variant="outline" onClick={handleAddTransaction} isDisabled={transactionCount >= MAX_TRANSACTIONS}>
                                    + Add Another Transaction
                                </Button>
                            </Box>
                        </>
                    ) : (
                        <Box border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
                            <TransactionForm
                                clientId={clientId}
                                entities={entities}
                                rowData={rowData}
                                transactionNumber={1}
                                ref={el => (formRefs.current[0] = el)}
                                onClose={onClose}
                                onValidationChange={(hasErrors: boolean) => handleFormValidation("single-form", hasErrors)}
                                onSubmitSuccess={() => handleFormSubmitSuccess("single-form")}
                            />
                        </Box>
                    )}
                </ModalBody>
                <ModalFooter>
                    {rowData ? (
                        <Button variant="ghost" colorScheme="red" onClick={handleDelete} isLoading={isDeletingTransaction} isDisabled={isDeletingTransaction}>
                            Delete Transaction
                        </Button>
                    ) : (
                        <span />
                    )}
                    <Button onClick={onClose} mr={3} variant="ghost">
                        Cancel
                    </Button>
                    <Button onClick={handleSubmitAllForms} variant="primary" isLoading={isAnyTransactionPending} isDisabled={isAnyTransactionPending}>
                        {rowData ? "Update" : `Add ${transactionCount} ${pluralize(transactionCount, "Transaction")}`}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};
